import React, { useState, useEffect } from 'react';
import { getReunionTypeShort, getReunionTypeLabel, formatReunionElectionLabel } from '../utils/reunionTypes';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useOrganisme } from '../context/OrganismeContext';
import {
  ArrowLeftIcon,
  UserPlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

export default function ModifierConseil() {
  const navigate = useNavigate();
  const { organismeActif } = useOrganisme();
  
  const [membresActuels, setMembresActuels] = useState([]);
  const [eligibles, setEligibles] = useState([]);
  const [reunions, setReunions] = useState([]);
  const [fractions, setFractions] = useState([]);
  const [fonctions, setFonctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Modifications en cours
  const [retraits, setRetraits] = useState([]); // [{membreId, raisonRetrait, raisonRetraitAutre, reunionRetraitId}]
  const [ajouts, setAjouts] = useState([]); // [{adherentId, estConseil, estBureau, estCoopte, fraction, fonction, reunionElectionId, dateCooptation}]
  const [modifications, setModifications] = useState([]); // [{membreId, fonction, reunionElectionId, dateCooptation}]
  
  // Modal ajout
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAdherent, setSelectedAdherent] = useState(null);
  const [addForm, setAddForm] = useState({
    fraction: '',
    ordre: '',
    reunionElectionId: '',
    estCoopte: false,
    dateCooptation: '',
    estMembreDeDroit: false,
  });

  // Modal retrait
  const [showRetraitModal, setShowRetraitModal] = useState(false);
  const [membreARetirer, setMembreARetirer] = useState(null);
  const [retraitForm, setRetraitForm] = useState({
    raisonRetrait: '',
    raisonRetraitAutre: '',
    reunionRetraitId: '',
    dateRetrait: '',
  });

  // Modal confirmation enregistrement
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [generatePdf, setGeneratePdf] = useState(false);

  const titreConseil = organismeActif === 'SAR' ? 'Conseil syndical' : 'Conseil d\'administration';
  
  // État pour la position sélectionnée
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [editingMembre, setEditingMembre] = useState(null);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organismeActif]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [compositionRes, eligiblesRes, reunionsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/conseil/${organismeActif}`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/eligibles`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/reunions`, { headers }),
      ]);

      // Charger les membres actifs du conseil
      let membresActifs = compositionRes.data.actifs.filter(m => m.type === 'conseil');
      
      // Charger aussi les membres inactifs du conseil pour le filtrage
      let membresInactifs = compositionRes.data.inactifs?.filter(m => m.type === 'conseil') || [];
      
      // Combiner actifs et inactifs pour le filtrage
      let tousMembres = [...membresActifs, ...membresInactifs];

      setMembresActuels(tousMembres);
      setEligibles(eligiblesRes.data);
      setReunions(reunionsRes.data);
      setFractions(compositionRes.data.fractions);
      setFonctions(compositionRes.data.fonctions);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRetraitModal = (membre) => {
    setMembreARetirer(membre);
    setRetraitForm({
      raisonRetrait: '',
      raisonRetraitAutre: '',
      reunionRetraitId: '',
      dateRetrait: '',
    });
    setShowRetraitModal(true);
  };

  const handleConfirmRetrait = () => {
    if (!retraitForm.raisonRetrait) {
      alert('Veuillez sélectionner une raison');
      return;
    }
    if (retraitForm.raisonRetrait === 'autre' && !retraitForm.raisonRetraitAutre) {
      alert('Veuillez préciser la raison');
      return;
    }

    setRetraits([...retraits, {
      membreId: membreARetirer._id,
      ...retraitForm,
      retirerDuConseil: true,  // Retirer uniquement du conseil
      retirerDuBureau: false,  // Ne PAS retirer du bureau
    }]);
    setShowRetraitModal(false);
    setMembreARetirer(null);
  };

  const handleCancelRetrait = (membreId) => {
    // Trouver le membre concerné pour récupérer sa position
    const membre = membresActuels.find(m => m._id === membreId);
    
    // Annuler le retrait
    setRetraits(retraits.filter(r => r.membreId !== membreId));
    
    // Annuler aussi l'ajout du remplaçant à la même position si il existe
    if (membre && membre.fraction && membre.ordre) {
      setAjouts(ajouts.filter(a => !(a.fraction === membre.fraction && a.ordre === membre.ordre)));
    }
  };

  const handleOpenAddModal = (fraction = '', ordre = '', isCoopte = false, isMembreDeDroit = false) => {
    setSelectedAdherent(null);
    setSearchTerm('');
    setAddForm({
      fraction,
      ordre: ordre.toString(),
      reunionElectionId: '',
      estCoopte: isCoopte,
      dateCooptation: '',
      estMembreDeDroit: isMembreDeDroit,
    });
    setSelectedPosition(fraction && ordre ? { fraction, ordre } : null);
    setEditingMembre(null);
    setShowAddModal(true);
  };

  const handleOpenEditModal = (membre) => {
    if (!membre || !membre.adherent) {
      console.error('Erreur: membre ou adherent est null', membre);
      return;
    }
    setSelectedAdherent({ _id: membre.adherent._id, prenom: membre.adherent.prenom, nom: membre.adherent.nom });
    setSearchTerm('');
    setAddForm({
      fraction: membre.fraction || '',
      ordre: membre.ordre ? membre.ordre.toString() : '',
      reunionElectionId: membre.reunionElection?._id || '',
      estCoopte: membre.estCoopte || false,
      dateCooptation: membre.dateCooptation || '',
      estMembreDeDroit: membre.estMembreDeDroit || false,
    });
    setSelectedPosition({ fraction: membre.fraction, ordre: membre.ordre });
    setEditingMembre(membre);
    setShowAddModal(true);
  };

  const handleConfirmAdd = () => {
    if (!selectedAdherent) {
      alert('Veuillez sélectionner un adhérent');
      return;
    }

    if (addForm.estCoopte) {
      if (!addForm.dateCooptation) {
        alert('Veuillez sélectionner une date de cooptation');
        return;
      }
    } else if (!addForm.estMembreDeDroit) {
      if (!addForm.reunionElectionId) {
        alert('Veuillez sélectionner une réunion d\'élection');
        return;
      }
    }

    // La fonction est optionnelle

    // Vérifier l'unicité fraction + ordre (si les deux sont définis)
    if (addForm.fraction && addForm.ordre) {
      const ordre = parseInt(addForm.ordre);
      
      // Vérifier dans les membres actuels (en excluant le membre en cours de modification ET ceux marqués pour retrait)
      const positionOccupeeActuelle = membresActuels.some(m => {
        const memePosition = m.fraction === addForm.fraction && m.ordre === ordre;
        const pasEnCoursModif = m._id !== editingMembre?._id;
        const pasMarqueRetrait = !retraits.some(r => r.membreId === m._id);
        return memePosition && pasEnCoursModif && pasMarqueRetrait;
      });
      
      // Vérifier dans les ajouts en attente
      const positionOccupeeAjouts = ajouts.some(
        a => a.fraction === addForm.fraction && a.ordre === ordre
      );
      
      if (positionOccupeeActuelle || positionOccupeeAjouts) {
        const fractionLabel = fractions.find(f => f.value === addForm.fraction)?.label || addForm.fraction;
        alert(`Cette position (${fractionLabel} - ordre ${ordre}) est déjà occupée`);
        return;
      }
    }

    if (editingMembre) {
      // Si c'est le même adhérent, on met à jour directement sans créer de retrait/ajout
      if (editingMembre.adherent._id === selectedAdherent._id) {
        // Mise à jour des informations du membre existant
        const updatedMembres = membresActuels.map(m => {
          if (m._id === editingMembre._id) {
            return {
              ...m,
              reunionElection: addForm.reunionElectionId ? reunions.find(r => r._id === addForm.reunionElectionId) : m.reunionElection,
              dateCooptation: addForm.estCoopte ? addForm.dateCooptation : m.dateCooptation,
            };
          }
          return m;
        });
        setMembresActuels(updatedMembres);
        
        // Ajouter ou mettre à jour dans la liste des modifications
        const modifIndex = modifications.findIndex(mod => mod.membreId === editingMembre._id);
        if (modifIndex >= 0) {
          const updatedModifications = [...modifications];
          updatedModifications[modifIndex] = {
            membreId: editingMembre._id,
            reunionElectionId: addForm.reunionElectionId || null,
            dateCooptation: addForm.estCoopte ? addForm.dateCooptation : null,
          };
          setModifications(updatedModifications);
        } else {
          setModifications([...modifications, {
            membreId: editingMembre._id,
            reunionElectionId: addForm.reunionElectionId || null,
            dateCooptation: addForm.estCoopte ? addForm.dateCooptation : null,
          }]);
        }
        
        setShowAddModal(false);
        setEditingMembre(null);
        return;
      } else {
        // Si l'adhérent change, on fait retrait + ajout
        setRetraits([...retraits, {
          membreId: editingMembre._id,
          raisonRetrait: 'modification',
          raisonRetraitAutre: 'Modification',
          reunionRetraitId: '',
        }]);
      }
    }

    setAjouts([...ajouts, {
      adherentId: selectedAdherent._id,
      adherentNom: `${selectedAdherent.prenom} ${selectedAdherent.nom}`,
      estCoopte: addForm.estCoopte,
      estMembreDeDroit: addForm.estMembreDeDroit,
      fraction: addForm.fraction || null,
      ordre: addForm.ordre ? parseInt(addForm.ordre) : null,
      fonction: null,
      reunionElectionId: (addForm.estCoopte || addForm.estMembreDeDroit) ? null : addForm.reunionElectionId,
      reunionElection: (addForm.estCoopte || addForm.estMembreDeDroit) ? null : reunions.find(r => r._id === addForm.reunionElectionId),
      dateCooptation: addForm.estCoopte ? addForm.dateCooptation : null,
    }]);
    setShowAddModal(false);
    setEditingMembre(null);
  };

  const handleCancelAdd = (adherentId) => {
    setAjouts(ajouts.filter(a => a.adherentId !== adherentId));
  };

  const handleSave = () => {
    if (retraits.length === 0 && ajouts.length === 0 && modifications.length === 0) {
      alert('Aucune modification à enregistrer');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      console.log('Envoi des données:', { ajouts, retraits, modifications, generatePdf });
      
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/masse`,
        { ajouts, retraits, modifications, generatePdf },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Réponse du serveur:', response.data);
      
      if (response.data.erreurs && response.data.erreurs.length > 0) {
        console.error('Erreurs lors de l\'enregistrement:', response.data.erreurs);
        alert(`Enregistrement partiel. Erreurs:\n${response.data.erreurs.map(e => `- ${e.erreur}`).join('\n')}`);
      } else {
        alert('Modifications enregistrées avec succès');
      }

      setShowConfirmModal(false);
      navigate(`/admin/composition-conseil`);
    } catch (err) {
      console.error('Erreur complète:', err);
      console.error('Réponse erreur:', err.response?.data);
      alert(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const filteredEligibles = eligibles.filter(e => {
    const nom = `${e.prenom} ${e.nom}`.toLowerCase();
    const matchSearch = nom.includes(searchTerm.toLowerCase());
    const notAlreadyAdded = !ajouts.some(a => a.adherentId === e._id);
    
    // Vérifier si l'adhérent est déjà ACTIF au conseil
    const membreConseil = membresActuels.find(m => {
      const adherentId = m.adherent?._id || m.adherent;
      return adherentId?.toString() === e._id?.toString() && m.actif === true;
    });
    
    // Inclure si : pas actif au conseil OU marqué pour retrait du conseil
    const disponible = !membreConseil || retraits.some(r => r.membreId === membreConseil._id);
    
    // Debug
    if (searchTerm && matchSearch) {
      console.log(`[FILTRE] ${e.prenom} ${e.nom}:`, {
        matchSearch,
        notAlreadyAdded,
        membreConseil: membreConseil ? 'OUI (actif au conseil)' : 'NON',
        disponible,
        RESULTAT: matchSearch && notAlreadyAdded && disponible ? '✅ AFFICHÉ' : '❌ MASQUÉ'
      });
    }
    
    return matchSearch && notAlreadyAdded && disponible;
  });

  const isMarkedForRetrait = (membreId) => retraits.some(r => r.membreId === membreId);

  // Calculer l'observation (durée restante) dynamiquement
  const getObservation = (fraction, reunionElection) => {
    if (!fraction) return '-';
    
    const configFractions = organismeActif === 'ADAR' ? {
      '1er_tiers': { anneeBase: 2020, duree: 3 },
      '2e_tiers': { anneeBase: 2024, duree: 3 },
      '3e_tiers': { anneeBase: 2022, duree: 3 },
    } : organismeActif === 'AMAIR' ? {
      '1er_tiers': { anneeBase: 2020, duree: 3 },
      '2e_tiers': { anneeBase: 2021, duree: 3 },
      '3e_tiers': { anneeBase: 2022, duree: 3 },
    } : {
      '1er_quart': { anneeBase: 2018, duree: 4 },
      '2e_quart': { anneeBase: 2019, duree: 4 },
      '3e_quart': { anneeBase: 2020, duree: 4 },
      '4e_quart': { anneeBase: 2021, duree: 4 },
    };
    
    const config = configFractions[fraction];
    if (!config) return '-';
    
    // Utiliser l'année d'élection comme dans getPeriode
    const anneeElection = reunionElection?.date 
      ? new Date(reunionElection.date).getFullYear() 
      : new Date().getFullYear();
    
    // Calculer l'année de fin du cycle basé sur l'année d'élection
    const cyclesPasses = Math.floor((anneeElection - config.anneeBase) / config.duree);
    const finPeriode = config.anneeBase + (cyclesPasses * config.duree) + config.duree - 1;
    
    // Calculer la durée restante par rapport à l'année actuelle
    const anneeActuelle = new Date().getFullYear();
    const dureeRestante = finPeriode - anneeActuelle;
    
    // Si la période est dépassée
    if (dureeRestante < 0) {
      return `Sortant à l'AG ${finPeriode + 1}`;
    } else if (dureeRestante === 0) {
      return 'Dernière année';
    } else if (dureeRestante === 1) {
      return 'Durée restante de 1 an';
    } else {
      return `Durée restante de ${dureeRestante} ans`;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <Link
          to="/admin/composition-conseil"
          className="inline-flex items-center text-indigo-600 hover:text-indigo-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Retour à la composition
        </Link>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Modifier la composition du {titreConseil}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {organismeActif}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Cliquez sur une ligne pour ajouter/modifier un membre à une position spécifique
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/admin/composition-conseil')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving || (retraits.length === 0 && ajouts.length === 0 && modifications.length === 0)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 flex items-center gap-2"
            >
              <CheckIcon className="h-5 w-5" />
              {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-8 mt-6 rounded-lg bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Résumé des modifications */}
      {(retraits.length > 0 || ajouts.length > 0) && (
        <div className="mx-8 mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
            <span className="font-medium text-yellow-800">Modifications en attente</span>
          </div>
          <p className="text-sm text-yellow-700">
            {retraits.length > 0 && `${retraits.length} retrait(s)`}
            {retraits.length > 0 && ajouts.length > 0 && ' et '}
            {ajouts.length > 0 && `${ajouts.length} ajout(s)`}
            {' '} - N'oubliez pas d'enregistrer
          </p>
        </div>
      )}

      <div className="px-8 py-6">
        {/* Tableau interactif de composition */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Composition du {titreConseil}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Cliquez sur une ligne pour ajouter ou modifier un membre
              </p>
            </div>
            {organismeActif === 'AMAIR' && (
              <button
                onClick={() => handleOpenAddModal('', '', true)}
                className="flex items-center gap-2 px-4 py-2 bg-honey-600 text-white rounded-lg hover:bg-honey-700"
              >
                <UserPlusIcon className="h-5 w-5" />
                Ajouter un membre coopté
              </button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adhérent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fonction</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Période</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Début</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Observation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(() => {
                  const fractionsValues = (organismeActif === 'AMAIR' || organismeActif === 'ADAR')
                    ? ['1er_tiers', '2e_tiers', '3e_tiers']
                    : ['1er_quart', '2e_quart', '3e_quart', '4e_quart'];
                  
                  const allPositions = [];
                  const maxOrdre = (organismeActif === 'AMAIR' || organismeActif === 'ADAR') ? 3 : 4; // AMAIR/ADAR: 3 ordres par tiers, SAR: 4 ordres par quart
                  fractionsValues.forEach(fraction => {
                    for (let ordre = 1; ordre <= maxOrdre; ordre++) {
                      allPositions.push({ fraction, ordre });
                    }
                  });

                  return allPositions.flatMap(({ fraction, ordre }) => {
                    const membre = membresActuels.find(m => m.fraction === fraction && m.ordre === ordre);
                    const ajoutEnCours = ajouts.find(a => a.fraction === fraction && a.ordre === ordre);
                    const isVacant = !membre && !ajoutEnCours;
                    const markedForRetrait = membre && isMarkedForRetrait(membre._id);
                    const fractionLabel = fractions.find(f => f.value === fraction)?.label || fraction;

                    const rows = [];
                    
                    // Ligne principale
                    rows.push(
                      <tr 
                        key={`${fraction}-${ordre}`} 
                        className={`${isVacant ? 'bg-gray-50 hover:bg-gray-100 cursor-pointer' : markedForRetrait ? 'bg-red-50' : 'hover:bg-gray-50'}`}
                        onClick={() => isVacant && handleOpenAddModal(fraction, ordre)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {fractionLabel} / {ordre}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {isVacant ? (
                            <span className="text-gray-400 italic">Vacant</span>
                          ) : ajoutEnCours ? (
                            <span className="text-green-700">{ajoutEnCours.adherentNom} <span className="text-xs">(à ajouter)</span></span>
                          ) : (
                            <span className={markedForRetrait ? 'text-red-700 line-through' : 'text-gray-900'}>
                              {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {ajoutEnCours?.fonction ? (
                            <span className="text-indigo-600 font-medium">
                              {fonctions.find(f => f.value === ajoutEnCours.fonction)?.label}
                            </span>
                          ) : membre?.fonction ? (
                            <span className="text-indigo-600 font-medium">
                              {fonctions.find(f => f.value === membre.fonction)?.label}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {isVacant ? '-' : (
                            <span className="text-gray-900">
                              {(() => {
                                const configFractions = organismeActif === 'ADAR' ? {
                                  '1er_tiers': { anneeBase: 2020, duree: 3 },
                                  '2e_tiers': { anneeBase: 2024, duree: 3 },
                                  '3e_tiers': { anneeBase: 2022, duree: 3 },
                                } : organismeActif === 'AMAIR' ? {
                                  '1er_tiers': { anneeBase: 2020, duree: 3 },
                                  '2e_tiers': { anneeBase: 2021, duree: 3 },
                                  '3e_tiers': { anneeBase: 2022, duree: 3 },
                                } : {
                                  '1er_quart': { anneeBase: 2018, duree: 4 },
                                  '2e_quart': { anneeBase: 2019, duree: 4 },
                                  '3e_quart': { anneeBase: 2020, duree: 4 },
                                  '4e_quart': { anneeBase: 2021, duree: 4 },
                                };
                                const frac = ajoutEnCours?.fraction || membre?.fraction;
                                const config = configFractions[frac];
                                if (!config) return '-';
                                const reunionElection = ajoutEnCours?.reunionElection || membre?.reunionElection;
                                const anneeElection = reunionElection?.date 
                                  ? new Date(reunionElection.date).getFullYear() 
                                  : new Date().getFullYear();
                                const cyclesPasses = Math.floor((anneeElection - config.anneeBase) / config.duree);
                                const debutPeriode = config.anneeBase + (cyclesPasses * config.duree);
                                const finPeriode = debutPeriode + config.duree - 1;
                                return `${debutPeriode}-${finPeriode}`;
                              })()}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {isVacant ? '-' : (
                            membre?.reunionElection?.date ? (
                              <span>
                                {formatReunionElectionLabel(membre.reunionElection, organismeActif)}
                              </span>
                            ) : membre?.dateCooptation ? (
                              new Date(membre.dateCooptation).toLocaleDateString('fr-FR')
                            ) : '-'
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {isVacant ? '-' : getObservation(ajoutEnCours?.fraction || membre?.fraction, ajoutEnCours?.reunionElection || membre?.reunionElection)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {isVacant ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenAddModal(fraction, ordre);
                              }}
                              className="text-green-600 hover:text-green-800 font-medium"
                            >
                              Ajouter
                            </button>
                          ) : (
                            <div className="flex items-center gap-2">
                              {ajoutEnCours ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelAdd(ajoutEnCours.adherentId);
                                  }}
                                  className="text-gray-600 hover:text-gray-800"
                                >
                                  Annuler
                                </button>
                              ) : markedForRetrait ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelRetrait(membre?._id);
                                  }}
                                  className="text-gray-600 hover:text-gray-800"
                                >
                                  Annuler retrait
                                </button>
                              ) : membre ? (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenEditModal(membre);
                                    }}
                                    className="text-indigo-600 hover:text-indigo-800"
                                  >
                                    Modifier
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenRetraitModal(membre);
                                    }}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    Retirer
                                  </button>
                                </>
                              ) : null}
                            </div>
                          )}
                        </td>
                      </tr>
                    );

                    // Si membre marqué pour retrait, ajouter une ligne "Vacant" pour le remplaçant
                    if (markedForRetrait) {
                      rows.push(
                        <tr 
                          key={`${fraction}-${ordre}-replacement`} 
                          className="bg-yellow-50 hover:bg-yellow-100 cursor-pointer border-l-4 border-yellow-400"
                          onClick={() => handleOpenAddModal(fraction, ordre)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {fractionLabel} / {ordre}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {ajoutEnCours ? (
                              <span className="text-green-700">{ajoutEnCours.adherentNom} <span className="text-xs">(remplaçant)</span></span>
                            ) : (
                              <span className="text-yellow-600 italic">Vacant (remplaçant)</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {ajoutEnCours?.fonction ? (
                              <span className="text-indigo-600 font-medium">
                                {fonctions.find(f => f.value === ajoutEnCours.fonction)?.label}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">-</td>
                          <td className="px-6 py-4 text-sm text-gray-500">-</td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {ajoutEnCours ? getObservation(ajoutEnCours.fraction, ajoutEnCours.reunionElection) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {ajoutEnCours ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelAdd(ajoutEnCours.adherentId);
                                }}
                                className="text-gray-600 hover:text-gray-800"
                              >
                                Annuler
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenAddModal(fraction, ordre);
                                }}
                                className="text-green-600 hover:text-green-800 font-medium"
                              >
                                Ajouter
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    }

                    return rows;
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>

        {/* Membres cooptés (AMAIR uniquement) */}
        {organismeActif === 'AMAIR' && membresActuels.filter(m => m.estCoopte).length > 0 && (
          <div className="bg-white rounded-lg shadow mt-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Membres Cooptés ({membresActuels.filter(m => m.estCoopte).length})
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {membresActuels.filter(m => m.estCoopte).map((membre) => {
                  const markedForRetrait = isMarkedForRetrait(membre._id);
                  return (
                    <div 
                      key={membre._id} 
                      className={`p-4 border rounded-lg ${markedForRetrait ? 'border-red-300 bg-red-50' : 'border-honey-200 bg-honey-50'}`}
                    >
                      <p className={`font-medium ${markedForRetrait ? 'text-red-700 line-through' : 'text-gray-900'}`}>
                        {membre.adherent?.prenom} {membre.adherent?.nom}
                      </p>
                      {membre.dateCooptation && (
                        <p className="text-xs text-honey-600 mt-1">
                          Coopté le {new Date(membre.dateCooptation).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                      <div className="mt-3 flex gap-2">
                        {markedForRetrait ? (
                          <button
                            onClick={() => handleCancelRetrait(membre._id)}
                            className="text-xs text-gray-600 hover:text-gray-800"
                          >
                            Annuler retrait
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setMembreARetirer(membre);
                              setShowRetraitModal(true);
                            }}
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Retirer
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Membres de droit (AMAIR uniquement) */}
        {organismeActif === 'AMAIR' && (
          <div className="bg-white rounded-lg shadow mt-6">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                Membre de Droit ({membresActuels.filter(m => m.estMembreDeDroit).length})
              </h2>
              <button
                onClick={() => handleOpenAddModal('', '', false, true)}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <UserPlusIcon className="h-5 w-5 mr-2" />
                Ajouter
              </button>
            </div>
            <div className="p-6">
              {membresActuels.filter(m => m.estMembreDeDroit).length === 0 && ajouts.filter(a => a.estMembreDeDroit).length === 0 ? (
                <p className="text-gray-500 italic">Aucun membre de droit</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {membresActuels.filter(m => m.estMembreDeDroit).map((membre) => {
                    const markedForRetrait = isMarkedForRetrait(membre._id);
                    return (
                      <div 
                        key={membre._id} 
                        className={`p-4 border rounded-lg ${markedForRetrait ? 'border-red-300 bg-red-50' : 'border-indigo-200 bg-indigo-50'}`}
                      >
                        <p className={`font-medium ${markedForRetrait ? 'text-red-700 line-through' : 'text-gray-900'}`}>
                          {membre.adherent?.prenom} {membre.adherent?.nom}
                        </p>
                        {membre.reunionElection && (
                          <p className="text-xs text-indigo-600 mt-1">
                            Élu le {new Date(membre.reunionElection.date).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                        <div className="mt-3 flex gap-2">
                          {markedForRetrait ? (
                            <button
                              onClick={() => handleCancelRetrait(membre._id)}
                              className="text-xs text-gray-600 hover:text-gray-800"
                            >
                              Annuler retrait
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setMembreARetirer(membre);
                                setShowRetraitModal(true);
                              }}
                              className="text-xs text-red-600 hover:text-red-800"
                            >
                              Retirer
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {ajouts.filter(a => a.estMembreDeDroit).map((ajout) => (
                    <div 
                      key={ajout.adherentId} 
                      className="p-4 border border-green-300 bg-green-50 rounded-lg"
                    >
                      <p className="font-medium text-green-700">
                        {ajout.adherentNom}
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        Nouveau membre de droit
                      </p>
                      <div className="mt-3">
                        <button
                          onClick={() => handleCancelAdd(ajout.adherentId)}
                          className="text-xs text-gray-600 hover:text-gray-800"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal Retrait */}
      {showRetraitModal && membreARetirer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Retirer {membreARetirer.adherent?.prenom} {membreARetirer.adherent?.nom}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Raison du retrait *
                </label>
                <select
                  value={retraitForm.raisonRetrait}
                  onChange={(e) => setRetraitForm({ ...retraitForm, raisonRetrait: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Sélectionner une raison</option>
                  <option value="demission">Démission</option>
                  <option value="non_adhesion">Non-adhésion</option>
                  <option value="fin_mandat">Fin de mandat</option>
                  <option value="autre">Autre (préciser)</option>
                </select>
              </div>

              {retraitForm.raisonRetrait === 'autre' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Précisez *
                  </label>
                  <input
                    type="text"
                    value={retraitForm.raisonRetraitAutre}
                    onChange={(e) => setRetraitForm({ ...retraitForm, raisonRetraitAutre: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Raison du retrait"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Réunion du retrait
                  </label>
                  <select
                    value={retraitForm.reunionRetraitId}
                    onChange={(e) => setRetraitForm({ ...retraitForm, reunionRetraitId: e.target.value, dateRetrait: '' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    disabled={!!retraitForm.dateRetrait}
                  >
                    <option value="">Aucune</option>
                    {reunions.map((r) => (
                      <option key={r._id} value={r._id}>
                        {new Date(r.date).toLocaleDateString('fr-FR')} - {getReunionTypeShort(r.type, organismeActif)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    OU Date de retrait
                  </label>
                  <input
                    type="date"
                    value={retraitForm.dateRetrait}
                    onChange={(e) => setRetraitForm({ ...retraitForm, dateRetrait: e.target.value, reunionRetraitId: '' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    disabled={!!retraitForm.reunionRetraitId}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Choisissez soit une réunion, soit une date de retrait
              </p>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowRetraitModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmRetrait}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Confirmer le retrait
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Enregistrement */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Composition finale
            </h3>
            
            <p className="text-gray-600 mb-4">
              Vous êtes sur le point d'enregistrer les modifications de la composition du conseil.
            </p>

            <div className="mb-6">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={generatePdf}
                  onChange={(e) => setGeneratePdf(e.target.checked)}
                  className="h-5 w-5 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Enregistrer la composition en PDF
                </span>
              </label>
              <p className="ml-8 mt-1 text-xs text-gray-500">
                Le PDF sera disponible dans la section "Documents de composition"
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setGeneratePdf(false);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={saving}
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmSave}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                disabled={saving}
              >
                {saving ? 'Enregistrement...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajout */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingMembre ? 'Modifier un membre' : addForm.estMembreDeDroit ? 'Ajouter un membre de droit' : addForm.estCoopte ? 'Ajouter un membre coopté' : 'Ajouter un membre au conseil'}
            </h3>

            <div className="space-y-4">
              {/* Recherche d'adhérent */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {editingMembre ? 'Adhérent' : 'Rechercher un adhérent *'}
                </label>
                
                {editingMembre ? (
                  // Mode modification : affichage en lecture seule
                  <div className="p-3 bg-gray-100 border border-gray-300 rounded-lg">
                    <span className="font-medium text-gray-700">
                      {selectedAdherent.prenom} {selectedAdherent.nom}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Pour changer l'adhérent, veuillez d'abord retirer ce membre puis en ajouter un nouveau
                    </p>
                  </div>
                ) : (
                  // Mode ajout : recherche normale
                  <>
                    <div className="relative">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Nom ou prénom..."
                      />
                    </div>
                    
                    {searchTerm && (
                      <div className="mt-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                        {filteredEligibles.slice(0, 10).map((e) => (
                          <button
                            key={e._id}
                            onClick={() => {
                              setSelectedAdherent(e);
                              setSearchTerm('');
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                          >
                            {e.prenom} {e.nom}
                          </button>
                        ))}
                        {filteredEligibles.length === 0 && (
                          <p className="px-3 py-2 text-gray-500 text-sm">Aucun adhérent trouvé</p>
                        )}
                      </div>
                    )}

                    {selectedAdherent && (
                      <div className="mt-2 flex items-center justify-between p-2 bg-indigo-50 border border-indigo-200 rounded-lg">
                        <span className="font-medium text-indigo-800">
                          {selectedAdherent.prenom} {selectedAdherent.nom}
                        </span>
                        <button
                          onClick={() => setSelectedAdherent(null)}
                          className="text-indigo-600 hover:text-indigo-800"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Option coopté (AMAIR uniquement, pas pour membre de droit) */}
              {organismeActif === 'AMAIR' && !addForm.estMembreDeDroit && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="estCoopte"
                    checked={addForm.estCoopte}
                    onChange={(e) => setAddForm({ ...addForm, estCoopte: e.target.checked })}
                    className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                  />
                  <label htmlFor="estCoopte" className="ml-2 text-sm text-gray-700">
                    Membre coopté
                  </label>
                </div>
              )}

              {/* Réunion d'élection (si pas coopté) */}
              {!addForm.estCoopte && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Réunion d'élection {addForm.estMembreDeDroit ? '' : '*'}
                  </label>
                  <select
                    value={addForm.reunionElectionId}
                    onChange={(e) => setAddForm({ ...addForm, reunionElectionId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Sélectionner une réunion</option>
                    {reunions.map((r) => (
                      <option key={r._id} value={r._id}>
                        {new Date(r.date).toLocaleDateString('fr-FR')} - {getReunionTypeLabel(r.type, organismeActif)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Date de cooptation (si coopté) */}
              {addForm.estCoopte && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de cooptation *
                  </label>
                  <input
                    type="date"
                    value={addForm.dateCooptation}
                    onChange={(e) => setAddForm({ ...addForm, dateCooptation: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              )}

              {/* Position sélectionnée (affichage uniquement) */}
              {selectedPosition && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <p className="text-sm text-indigo-800">
                    <span className="font-medium">Position :</span> {fractions.find(f => f.value === selectedPosition.fraction)?.label || selectedPosition.fraction} - Ordre {selectedPosition.ordre}
                  </p>
                </div>
              )}

            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmAdd}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
